import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Ad {
  id: string;
  title: string;
  description: string | null;
  image_url: string;
  link_url: string | null;
}

export function AdDisplay() {
  const [ads, setAds] = useState<Ad[]>([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [hasClosedOnce, setHasClosedOnce] = useState(false);

  useEffect(() => {
    loadAds();
  }, []);

  useEffect(() => {
    if (ads.length === 0) return;

    // Rotate ads every 60 seconds (1 minute)
    const interval = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % ads.length);
      setIsVisible(true); // Show ad again after rotation
    }, 60000);

    return () => clearInterval(interval);
  }, [ads.length]);

  const loadAds = async () => {
    try {
      const { data, error } = await supabase
        .from('ads')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      if (data && data.length > 0) {
        setAds(data);
      }
    } catch (error) {
      console.error('Error loading ads:', error);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setHasClosedOnce(true);
  };

  if (ads.length === 0 || !isVisible) {
    return null;
  }

  const currentAd = ads[currentAdIndex];

  const AdContent = () => (
    <div className="relative bg-card border border-border rounded-lg shadow-lg overflow-hidden max-w-2xl mx-auto">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full bg-background/80 hover:bg-background"
        onClick={handleClose}
      >
        <X className="h-4 w-4" />
      </Button>
      
      <div className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <img
            src={currentAd.image_url}
            alt={currentAd.title}
            className="w-full md:w-48 h-48 object-cover rounded"
          />
          <div className="flex-1">
            <h3 className="text-xl font-bold text-foreground mb-2">
              {currentAd.title}
            </h3>
            {currentAd.description && (
              <p className="text-muted-foreground mb-4">
                {currentAd.description}
              </p>
            )}
            {currentAd.link_url && (
              <a
                href={currentAd.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button>En savoir plus</Button>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-fade-in">
      {currentAd.link_url ? (
        <AdContent />
      ) : (
        <AdContent />
      )}
    </div>
  );
}
